import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Avatar,
  Badge,
  IconButton,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  SimpleGrid,
  Image,
  Link,
  VStack,
  Alert,
  Icon,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiEdit,
  FiEye,
  FiUploadCloud,
  FiInfo,
  FiPlus,
} from "react-icons/fi";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";

const PAGE_SIZE = 5;

const initialLeaguesData = [
  {
    name: "NHL Preseason",
    category: "Ice Hockey",
    shortName: "NHL Preseason",
    apiSportKey: "icehockey_nhl_preseason",
    slug: "icehockey-nhl-preseason",
    hasOutrights: "No",
    inSeason: "Yes",
    status: "Enabled",
  },
  {
    name: "ICC Women's World Cup",
    category: "Cricket",
    shortName: "Women's World Cup",
    apiSportKey: "cricket_womens_world_cup",
    slug: "cricket-womens-world-cup",
    hasOutrights: "Yes",
    inSeason: "Yes",
    status: "Enabled",
  },
];

const categories = [
  "Ice Hockey",
  "Cricket",
  "Soccer",
  "Basketball",
  "Rugby League",
];

const InSeasonLeagues = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isFilterOpen,
    onOpen: onFilterOpen,
    onClose: onFilterClose,
  } = useDisclosure();

  const [leagues, setLeagues] = useState(initialLeaguesData);
  const [searchTerm, setSearchTerm] = useState("");
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Filter States
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterOutrights, setFilterOutrights] = useState("All");
  const [filterApiKey, setFilterApiKey] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  const [formData, setFormData] = useState({
    name: "",
    shortName: "",
    category: "",
    apiSportKey: "",
    slug: "",
    hasOutrights: "No",
    inSeason: "Yes",
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleEdit = (league) => {
    setEditData(league);
    setFormData({
      name: league.name,
      shortName: league.shortName,
      category: league.category,
      apiSportKey: league.apiSportKey,
      slug: league.slug,
      hasOutrights: league.hasOutrights,
      inSeason: league.inSeason,
    });
    setImagePreview(null);
    onOpen();
  };

  const handleAddNew = () => {
    setEditData(null);
    setFormData({
      name: "",
      shortName: "",
      category: "",
      apiSportKey: "",
      slug: "",
      hasOutrights: "No",
      inSeason: "Yes",
    });
    setImagePreview(null);
    onOpen();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (editData) {
      setLeagues((prev) =>
        prev.map((l) => (l.slug === editData.slug ? { ...l, ...formData } : l))
      );
    } else {
      setLeagues((prev) => [...prev, { ...formData, status: "Enabled" }]);
    }
    onClose();
  };

  const filteredData = leagues.filter((l) => {
    const matchesSearch =
      l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === "All" || l.category === filterCategory;
    const matchesOutrights =
      filterOutrights === "All" || l.hasOutrights === filterOutrights;
    const matchesStatus =
      filterStatus === "All" || l.status === filterStatus;
    const matchesApiKey =
      filterApiKey === "All" ||
      (filterApiKey === "Yes" ? !!l.apiSportKey : !l.apiSportKey);

    return (
      matchesSearch &&
      matchesCategory &&
      matchesOutrights &&
      matchesStatus &&
      matchesApiKey
    );
  });

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const start = (page - 1) * PAGE_SIZE;
  const visibleData = filteredData.slice(start, start + PAGE_SIZE);
  const SIDEBAR_WIDTH = "240px";

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="#f8f9fd"
        minH="100vh"
        mt="80px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        {/* Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          mb={8}
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="700" color="#344767">
            In Season Leagues
          </Text>

          <Button
            bg="#5e72e4"
            color="white"
            leftIcon={<FiPlus />}
            onClick={handleAddNew}
            h="45px"
            px={6}
            borderRadius="8px"
            fontSize="sm"
            _hover={{ bg: "#4e62d4" }}
          >
            Add New League
          </Button>
        </Flex>

        {/* Search & Actions */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="flex-end"
          gap={4}
          mb={6}
          align={{ base: "stretch", md: "center" }}
        >
          <InputGroup maxW={{ base: "100%", md: "300px" }} size="md">
            <Input
              placeholder="Search name or slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
              borderRadius="8px"
              border="1px solid #e2e8f0"
              fontSize="sm"
              _focus={{
                borderColor: "#5e72e4",
                boxShadow: "0 0 0 1px #5e72e4",
              }}
            />
            <InputRightElement color="gray.400">
              <FiSearch />
            </InputRightElement>
          </InputGroup>

          <Flex gap={3}>
            <Button
              variant="outline"
              borderColor="#e2e8f0"
              borderRadius="8px"
              fontSize="sm"
              bg="white"
              h="40px"
              flex={1}
              _hover={{ bg: "gray.50" }}
            >
              Bulk Action
            </Button>

            <Button
              bg="#11cdef"
              color="white"
              fontSize="sm"
              borderRadius="8px"
              h="40px"
              flex={1}
              _hover={{ bg: "#0fa8c7" }}
              onClick={onFilterOpen}
            >
              Filter
            </Button>
          </Flex>
        </Flex>

        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
          overflow="hidden"
          border="1px solid #f0f2f5"
          display={{ base: "none", md: "block" }}
        >
          <TableContainer overflowX="auto">
            <Table variant="simple" size={{ base: "sm", md: "md" }}>
              <Thead bg="#1f15da">
                <Tr>
                  <Th border="none">
                    <Checkbox colorScheme="whiteAlpha" />
                  </Th>
                  <Th color="white" fontSize="xs" py={4} border="none">NAME</Th>
                  <Th color="white" fontSize="xs" py={4} border="none">CATEGORY</Th>
                  <Th color="white" fontSize="xs" py={4} border="none">OUTRIGHTS</Th>
                  <Th color="white" fontSize="xs" py={4} border="none">IN SEASON</Th>
                  <Th color="white" fontSize="xs" py={4} border="none">STATUS</Th>
                  <Th color="white" fontSize="xs" py={4} border="none" textAlign="right">ACTION</Th>
                </Tr>
              </Thead>

              <Tbody>
                {visibleData.length > 0 ? (
                  visibleData.map((item, i) => (
                    <Tr key={i} _hover={{ bg: "#f8f9fa" }}>
                      <Td>
                        <Checkbox />
                      </Td>

                      <Td py={4}>
                        <Flex align="center" gap={3}>
                          <Avatar
                            name={item.name}
                            size="sm"
                            bg="#5e72e4"
                            color="white"
                            borderRadius="8px"
                          />
                          <Text fontWeight="700" color="#344767" fontSize="sm">{item.name}</Text>
                        </Flex>
                      </Td>

                      <Td py={4} color="#67748e" fontSize="xs">{item.category}</Td>
                      <Td py={4}>
                        <Badge
                          bg={item.hasOutrights === "Yes" ? "blue.50" : "gray.50"}
                          color={item.hasOutrights === "Yes" ? "blue.500" : "gray.500"}
                          px={3}
                          borderRadius="full"
                          fontSize="10px"
                          fontWeight="700"
                        >
                          {item.hasOutrights}
                        </Badge>
                      </Td>

                      <Td py={4}>
                        <Badge
                          bg={item.inSeason === "Yes" ? "rgba(45, 206, 137, 0.1)" : "rgba(251, 99, 64, 0.1)"}
                          color={item.inSeason === "Yes" ? "#2dce89" : "#fb6340"}
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="10px"
                          fontWeight="700"
                        >
                          {item.inSeason === "Yes" ? "In Season" : "Closed"}
                        </Badge>
                      </Td>

                      <Td py={4}>
                        <Badge
                          bg={item.status === "Enabled" ? "rgba(45, 206, 137, 0.1)" : "rgba(251, 99, 64, 0.1)"}
                          color={item.status === "Enabled" ? "#2dce89" : "#fb6340"}
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="10px"
                          fontWeight="700"
                        >
                          {item.status}
                        </Badge>
                      </Td>

                      <Td py={4} textAlign="right">
                        <Flex justify="flex-end" gap={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => handleEdit(item)}
                            borderColor="#e2e8f0"
                            borderRadius="6px"
                            fontSize="xs"
                            leftIcon={<FiEdit />}
                            _hover={{ bg: "gray.50" }}
                          >
                            Edit
                          </Button>

                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme={item.status === "Enabled" ? "red" : "green"}
                            borderRadius="6px"
                            fontSize="xs"
                            leftIcon={<FiEye />}
                          >
                            {item.status === "Enabled" ? "Disable" : "Enable"}
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={12}>
                      <Text color="#8898aa" fontSize="sm">No leagues found matching your criteria</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* MOBILE CARD VIEW */}
        <VStack display={{ base: "flex", md: "none" }} spacing={4} align="stretch">
          {filteredData.length > 0 ? (
            visibleData.map((item, index) => (
              <Box
                key={index}
                bg="white"
                p={4}
                borderRadius="12px"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.200"
              >
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="700" color="#344767">
                    Name
                  </Text>
                  <Flex align="center" gap={2}>
                    <Avatar
                      name={item.name}
                      bg="#5e72e4"
                      color="white"
                      size="xs"
                      borderRadius="8px"
                    />
                    <Text fontSize="sm" color="#344767" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Flex>
                </Flex>
                <Box borderBottom="1px solid" borderColor="gray.100" mb={3} />

                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="700" color="#344767">
                    Category
                  </Text>
                  <Text fontSize="sm" color="#67748e">
                    {item.category}
                  </Text>
                </Flex>
                <Box borderBottom="1px solid" borderColor="gray.100" mb={3} />

                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="700" color="#344767">
                    Outrights
                  </Text>
                  <Badge
                    bg={item.hasOutrights === "Yes" ? "blue.50" : "gray.50"}
                    color={item.hasOutrights === "Yes" ? "blue.500" : "gray.500"}
                    px={3}
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                  >
                    {item.hasOutrights}
                  </Badge>
                </Flex>
                <Box borderBottom="1px solid" borderColor="gray.100" mb={3} />

                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="700" color="#344767">
                    In Season
                  </Text>
                  <Badge
                    bg={item.inSeason === "Yes" ? "rgba(45, 206, 137, 0.1)" : "rgba(251, 99, 64, 0.1)"}
                    color={item.inSeason === "Yes" ? "#2dce89" : "#fb6340"}
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                  >
                    {item.inSeason === "Yes" ? "In Season" : "Closed"}
                  </Badge>
                </Flex>
                <Box borderBottom="1px solid" borderColor="gray.100" mb={3} />

                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="700" color="#344767">
                    Status
                  </Text>
                  <Badge
                    bg={
                      item.status === "Enabled"
                        ? "rgba(45, 206, 137, 0.1)"
                        : "rgba(251, 99, 64, 0.1)"
                    }
                    color={item.status === "Enabled" ? "#2dce89" : "#fb6340"}
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                  >
                    {item.status}
                  </Badge>
                </Flex>
                <Box borderBottom="1px solid" borderColor="gray.100" mb={3} />

                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="700" color="#344767">
                    Action
                  </Text>
                  <Flex gap={2}>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => handleEdit(item)}
                      borderColor="#e2e8f0"
                      borderRadius="6px"
                      fontSize="xs"
                      leftIcon={<FiEdit />}
                      _hover={{ bg: "gray.50" }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme={item.status === "Enabled" ? "red" : "green"}
                      borderRadius="6px"
                      fontSize="xs"
                      leftIcon={<FiEye />}
                    >
                      {item.status === "Enabled" ? "Disable" : "Enable"}
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            ))
          ) : (
            <Box textAlign="center" py={12} bg="white" borderRadius="12px" border="1px solid" borderColor="gray.200">
              <Text color="#8898aa" fontSize="sm">No leagues found matching your criteria</Text>
            </Box>
          )}
        </VStack>

        {/* Pagination */}
        {totalPages > 1 && (
          <Flex justify="center" mt={8} gap={{ base: 2, md: 3 }} flexWrap="wrap">
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              isDisabled={page === 1}
              variant="outline"
              borderColor="#e2e8f0"
              bg="white"
              borderRadius="md"
              _hover={{ bg: "gray.50" }}
            >
              {"<"}
            </Button>
            {[...Array(totalPages)].map((_, i) => (
              <Button
                key={i}
                size="sm"
                variant={page === i + 1 ? "solid" : "outline"}
                bg={page === i + 1 ? "#5e72e4" : "white"}
                color={page === i + 1 ? "white" : "#67748e"}
                borderColor={page === i + 1 ? "#5e72e4" : "#e2e8f0"}
                onClick={() => setPage(i + 1)}
                borderRadius="md"
                minW={{ base: "32px", md: "40px" }}
              >
                {i + 1}
              </Button>
            ))}
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              isDisabled={page === totalPages}
              variant="outline"
              borderColor="#e2e8f0"
              bg="white"
              borderRadius="md"
              _hover={{ bg: "gray.50" }}
            >
              {">"}
            </Button>
          </Flex>
        )}

        {/* ADD/EDIT MODAL */}
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "4xl" }} isCentered scrollBehavior="inside">
          <ModalOverlay backdropFilter="blur(4px)" />
          <ModalContent borderRadius={{ base: 0, md: "16px" }} mx={{ base: 0, md: 4 }}>
            <ModalHeader borderBottom="1px solid #f0f2f5" py={4} color="#344767">
              <Text fontSize="lg" fontWeight="700">
                {editData ? "Edit League" : "Add New League"}
              </Text>
            </ModalHeader>
            <ModalCloseButton top={4} />
            <ModalBody p={{ base: 4, md: 8 }}>
              <VStack spacing={6} align="stretch">
                <Alert
                  bg="blue.50"
                  borderRadius="12px"
                  p={4}
                  alignItems="start"
                >
                  <Box>
                    <Text fontWeight="700" fontSize="sm" mb={1} color="blue.800">
                      Automatic Add Info
                    </Text>
                    <Text fontSize="xs" lineHeight="tall" color="blue.700">
                      Leagues will be automatically fetched when the Fetch
                      Leagues cron job runs on the server, but you must manually
                      enable them to control API costs. You can also add leagues manually.
                      <br />
                      <Text as="span" color="red.500" fontWeight="700">
                        API Sport Key must match the Sports API's "Sport Key" exactly.
                      </Text>{" "}
                      <Link color="#5e72e4" fontWeight="700">
                        Get your API Sport Key here
                      </Link>
                    </Text>
                  </Box>
                </Alert>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 10 }}>
                  {/* Left Column: Image */}
                  <Box>
                    <FormLabel fontSize="xs" fontWeight="700" color="#525f7f" mb={2}>LEAGUE LOGO</FormLabel>
                    <Box
                      border="2px dashed"
                      borderColor="#e2e8f0"
                      borderRadius="12px"
                      p={4}
                      position="relative"
                      bg="#f8f9fd"
                      textAlign="center"
                    >
                      <Input
                        type="file"
                        ref={fileInputRef}
                        display="none"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      <Image
                        src={
                          imagePreview ||
                          "https://via.placeholder.com/150x150?text=Upload+Logo"
                        }
                        alt="League Preview"
                        w="100%"
                        h={{ base: "200px", md: "300px" }}
                        objectFit="contain"
                        borderRadius="8px"
                        mb={4}
                      />
                      <Button
                        leftIcon={<FiUploadCloud />}
                        bg="white"
                        size="sm"
                        boxShadow="sm"
                        onClick={triggerFileUpload}
                        borderRadius="8px"
                        fontSize="xs"
                      >
                        Upload New Image
                      </Button>
                    </Box>
                    <Text fontSize="xs" color="#8898aa" mt={4} lineHeight="tall">
                      Supported Files:{" "}
                      <Text as="span" fontWeight="700">.png, .jpg, .jpeg.</Text>
                      {" "}Resized to <Text as="span" fontWeight="700">150x150px</Text>
                    </Text>
                  </Box>

                  {/* Right Column: Form Fields */}
                  <VStack spacing={4} align="stretch">
                    <FormControl isRequired>
                      <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">CATEGORY</FormLabel>
                      <Select
                        placeholder="Select Category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        h="45px"
                        borderRadius="8px"
                        borderColor="#e2e8f0"
                        fontSize="sm"
                        _focus={{ borderColor: "#5e72e4", boxShadow: "0 0 0 1px #5e72e4" }}
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">NAME</FormLabel>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="NHL Preseason"
                        h="45px"
                        borderRadius="8px"
                        borderColor="#e2e8f0"
                        fontSize="sm"
                        _focus={{ borderColor: "#5e72e4", boxShadow: "0 0 0 1px #5e72e4" }}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">SHORT NAME</FormLabel>
                      <Input
                        name="shortName"
                        value={formData.shortName}
                        onChange={handleChange}
                        placeholder="NHL Preseason"
                        h="45px"
                        borderRadius="8px"
                        borderColor="#e2e8f0"
                        fontSize="sm"
                        _focus={{ borderColor: "#5e72e4", boxShadow: "0 0 0 1px #5e72e4" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">API SPORT KEY</FormLabel>
                      <Input
                        name="apiSportKey"
                        value={formData.apiSportKey}
                        onChange={handleChange}
                        placeholder="icehockey_nhl_preseason"
                        h="45px"
                        borderRadius="8px"
                        bg="#f8f9fd"
                        borderColor="#e2e8f0"
                        fontSize="sm"
                        _focus={{ borderColor: "#5e72e4", boxShadow: "0 0 0 1px #5e72e4" }}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">SLUG</FormLabel>
                      <Input
                        name="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        placeholder="icehockey-nhl-preseason"
                        h="45px"
                        borderRadius="8px"
                        borderColor="#e2e8f0"
                        fontSize="sm"
                        _focus={{ borderColor: "#5e72e4", boxShadow: "0 0 0 1px #5e72e4" }}
                      />
                      <Text fontSize="10px" color="red.400" mt={1} fontWeight="600">
                        * Spaces are not allowed
                      </Text>
                    </FormControl>

                    <FormControl>
                      <Flex align="center" gap={2}>
                        <FormLabel fontSize="xs" fontWeight="700" color="#525f7f" mb={0}>HAS OUTRIGHTS</FormLabel>
                        <Icon as={FiInfo} color="gray.400" />
                      </Flex>
                      <Select
                        name="hasOutrights"
                        value={formData.hasOutrights}
                        onChange={handleChange}
                        h="45px"
                        borderRadius="8px"
                        borderColor="#e2e8f0"
                        fontSize="sm"
                        _focus={{ borderColor: "#5e72e4", boxShadow: "0 0 0 1px #5e72e4" }}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </Select>
                      <Text fontSize="10px" color="red.400" mt={1} fontWeight="600">
                        * You can't change this for automatically added leagues.
                      </Text>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">IN SEASON</FormLabel>
                      <Select
                        name="inSeason"
                        value={formData.inSeason}
                        onChange={handleChange}
                        h="45px"
                        borderRadius="8px"
                        borderColor="#e2e8f0"
                        fontSize="sm"
                        _focus={{ borderColor: "#5e72e4", boxShadow: "0 0 0 1px #5e72e4" }}
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Select>
                      <Text fontSize="10px" color="#8898aa" mt={1}>
                        * Automatically managed if API Key is valid.
                      </Text>
                    </FormControl>
                  </VStack>
                </SimpleGrid>

                <Button
                  bg="#5e72e4"
                  color="white"
                  w="100%"
                  h="45px"
                  fontSize="sm"
                  fontWeight="700"
                  borderRadius="8px"
                  _hover={{ bg: "#4e62d4" }}
                  onClick={handleSubmit}
                  mt={4}
                >
                  Save League
                </Button>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* FILTER DRAWER */}
        <Drawer
          isOpen={isFilterOpen}
          placement="right"
          onClose={onFilterClose}
          size="sm"
        >
          <DrawerOverlay backdropFilter="blur(4px)" />
          <DrawerContent>
            <DrawerCloseButton top={4} />
            <DrawerHeader borderBottomWidth="1px" color="#344767" fontWeight="700">Filter Data</DrawerHeader>

            <DrawerBody py={6}>
              <VStack spacing={6} align="stretch">
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">CATEGORY</FormLabel>
                  <Select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    h="45px"
                    borderRadius="8px"
                  >
                    <option value="All">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">HAS OUTRIGHTS</FormLabel>
                  <Select
                    value={filterOutrights}
                    onChange={(e) => setFilterOutrights(e.target.value)}
                    h="45px"
                    borderRadius="8px"
                  >
                    <option value="All">All</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="700" color="#525f7f">HAS API SPORT KEY</FormLabel>
                  <Select
                    value={filterApiKey}
                    onChange={(e) => setFilterApiKey(e.target.value)}
                    h="45px"
                    borderRadius="8px"
                  >
                    <option value="All">All</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Select>
                </FormControl>
              </VStack>
            </DrawerBody>

            <DrawerFooter borderTopWidth="1px" p={6}>
              <Button
                bg="#5e72e4"
                color="white"
                w="100%"
                h="45px"
                borderRadius="8px"
                onClick={onFilterClose}
                _hover={{ bg: "#4e62d4" }}
              >
                Apply Filters
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </>
  );
};

export default InSeasonLeagues;
